import { MENTION_NODE, MENTION_TYPE_ATTR, inferMentionableType } from "./mention-types";
import type { JSONContent, Mark, MentionableType } from "./mention-types";

// Server-safe helpers over mention documents: parsing, mention-id extraction,
// and plain-text / markdown serialization. Imported by the worker and the agent
// prompt builder, so this module must stay free of React and Tiptap imports.

export const MARKDOWN_MENTION_PATH_PREFIX = "/__mentions__";

const MENTIONABLE_TYPES: MentionableType[] = ["agent", "user", "tool"];

function isMentionableType(value: string): value is MentionableType {
  return (MENTIONABLE_TYPES as string[]).includes(value);
}

/**
 * Parse a mention href (`/__mentions__/agent/agt_…` or legacy `mention:…`)
 * into type + id. Returns null when the href is not a mention.
 */
export function parseMentionHref(href: string): { type: MentionableType; id: string } | null {
  if (href.startsWith(`${MARKDOWN_MENTION_PATH_PREFIX}/`)) {
    const stripped = href.slice(MARKDOWN_MENTION_PATH_PREFIX.length + 1);
    const [maybeType, ...idParts] = stripped.split("/");
    if (maybeType && isMentionableType(maybeType) && idParts.length > 0) {
      return { type: maybeType, id: idParts.join("/") };
    }
    return null;
  }
  if (!href.startsWith("mention:")) return null;
  const stripped = href.slice("mention:".length);
  const colonIdx = stripped.indexOf(":");
  if (colonIdx !== -1) {
    const maybeType = stripped.slice(0, colonIdx);
    if (isMentionableType(maybeType)) {
      return { type: maybeType, id: stripped.slice(colonIdx + 1) };
    }
  }
  return {
    type: inferMentionableType(stripped),
    id: stripped,
  };
}

function escapeMdLinkLabel(text: string): string {
  return text.replace(/[\\[\]]/g, "\\$&");
}

function unescapeMdLinkLabel(text: string): string {
  return text.replace(/\\([\\[\]])/g, "$1");
}

export function mentionLinkMarkdown(id: string, label: string, type: MentionableType): string {
  const display = label.startsWith("@") ? label : `@${label}`;
  return `[${escapeMdLinkLabel(display)}](${MARKDOWN_MENTION_PATH_PREFIX}/${type}/${id})`;
}

/** Inverse of the label half of {@link mentionLinkMarkdown}. */
export function mentionLabelFromLinkText(raw: string): string {
  return unescapeMdLinkLabel(raw).replace(/^@/, "");
}

// ============================================================================
// Parse (backward compat with plain text)
// ============================================================================

export function parseEditorContent(value: string | null | undefined): JSONContent | null {
  if (typeof value !== "string" || !value.trim().startsWith("{")) return null;
  try {
    const parsed: unknown = JSON.parse(value);
    return typeof parsed === "object" && parsed !== null && (parsed as JSONContent).type === "doc"
      ? (parsed as JSONContent)
      : null;
  } catch {
    return null;
  }
}

// ============================================================================
// Extract mentioned IDs
// ============================================================================

/**
 * Collects the `id` of every mention chip in a stored comment, in document
 * order. Returns an empty array when the content is not a mention document.
 *
 * NOTE: this does NOT deduplicate — callers apply their own dedup (e.g.
 * `new Set(...)`) when they need it.
 */
export function extractMentionIds(content: string): string[] {
  const doc = parseEditorContent(content);
  if (doc) {
    const ids: string[] = [];
    collectMentionIds(doc, ids);
    return ids;
  }
  return extractMentionIdsFromMarkdown(content);
}

const MARKDOWN_MENTION_HREF = /\]\((\/__mentions__\/(?:agent|user|tool)\/[^)]+|mention:[^)]+)\)/g;

function extractMentionIdsFromMarkdown(content: string): string[] {
  const ids: string[] = [];
  const pattern = new RegExp(MARKDOWN_MENTION_HREF.source, "g");
  for (let match = pattern.exec(content); match; match = pattern.exec(content)) {
    const parsed = parseMentionHref(match[1] ?? "");
    if (parsed) ids.push(parsed.id);
  }
  return ids;
}

function collectMentionIds(node: JSONContent, ids: string[]): void {
  if (node.type === MENTION_NODE && node.attrs?.id) {
    ids.push(String(node.attrs.id));
  }
  for (const child of node.content ?? []) {
    collectMentionIds(child, ids);
  }
}

// ============================================================================
// Shared mention helpers
// ============================================================================

function getMentionLabel(attrs: Record<string, unknown> | undefined): string {
  const raw = String(attrs?.label ?? attrs?.username ?? attrs?.id ?? "");
  return raw.startsWith("@") ? raw : `@${raw}`;
}

function getMentionTypeFromAttrs(attrs: Record<string, unknown> | undefined): MentionableType {
  const type = attrs?.[MENTION_TYPE_ATTR];
  if (type === "agent" || type === "user" || type === "tool") return type;
  return inferMentionableType(String(attrs?.id ?? ""));
}

// ============================================================================
// Convert to plain text
// ============================================================================

export function getPlainText(content: string): string {
  const doc = parseEditorContent(content);
  if (!doc) return content;

  return collectMarkdownBlocks(doc, false).join("\n\n");
}

// ============================================================================
// Convert to markdown text with mention links
// ============================================================================

/**
 * Extracts text from Tiptap JSON content, preserving mentions as
 * markdown links with a safe internal path so they can be rendered
 * as pills by the MarkdownRenderer component.
 *
 * If the content is not valid Tiptap JSON, it is returned as-is
 * (assumed to already be markdown/plain text).
 */
export function getMarkdownText(content: string): string {
  const doc = parseEditorContent(content);
  if (!doc) return content;

  return collectMarkdownBlocks(doc, true).join("\n\n");
}

function collectMarkdownBlocks(
  node: JSONContent,
  preserveMentionLinks: boolean,
  listDepth = 0,
): string[] {
  const blocks: string[] = [];

  switch (node.type) {
    case "doc":
      for (const child of node.content ?? []) {
        blocks.push(...collectMarkdownBlocks(child, preserveMentionLinks, listDepth));
      }
      break;

    case "paragraph": {
      const text = collectInlineMarkdown(node, preserveMentionLinks);
      blocks.push(text);
      break;
    }

    case "heading": {
      const level = Number(node.attrs?.level ?? 1);
      const prefix = "#".repeat(Math.min(level, 6));
      const text = collectInlineMarkdown(node, preserveMentionLinks);
      blocks.push(`${prefix} ${text}`);
      break;
    }

    case "codeBlock": {
      const lang = String(node.attrs?.language ?? "");
      const text = collectInlineMarkdown(node, false);
      blocks.push(`\`\`\`${lang}\n${text}\n\`\`\``);
      break;
    }

    case "blockquote": {
      const inner = collectMarkdownBlocks(
        { type: "doc", content: node.content ?? [] },
        preserveMentionLinks,
        listDepth,
      );
      blocks.push(inner.map((line) => `> ${line}`).join("\n> \n"));
      break;
    }

    case "bulletList": {
      for (const item of node.content ?? []) {
        const indent = "  ".repeat(listDepth);
        const itemBlocks = collectMarkdownBlocks(item, preserveMentionLinks, listDepth + 1);
        const first = itemBlocks[0] ?? "";
        const rest = itemBlocks.slice(1);
        blocks.push([`${indent}- ${first}`, ...rest.map((b) => `${indent}  ${b}`)].join("\n"));
      }
      break;
    }

    case "orderedList": {
      let index = Number(node.attrs?.start ?? 1);
      for (const item of node.content ?? []) {
        const indent = "  ".repeat(listDepth);
        const itemBlocks = collectMarkdownBlocks(item, preserveMentionLinks, listDepth + 1);
        const first = itemBlocks[0] ?? "";
        const rest = itemBlocks.slice(1);
        blocks.push(
          [`${indent}${index}. ${first}`, ...rest.map((b) => `${indent}   ${b}`)].join("\n"),
        );
        index++;
      }
      break;
    }

    case "listItem":
    case "taskItem":
      for (const child of node.content ?? []) {
        blocks.push(...collectMarkdownBlocks(child, preserveMentionLinks, listDepth));
      }
      break;

    case "taskList":
      for (const item of node.content ?? []) {
        const indent = "  ".repeat(listDepth);
        const checked = item.attrs?.checked ? "x" : " ";
        const itemBlocks = collectMarkdownBlocks(item, preserveMentionLinks, listDepth + 1);
        const first = itemBlocks[0] ?? "";
        const rest = itemBlocks.slice(1);
        blocks.push(
          [`${indent}- [${checked}] ${first}`, ...rest.map((b) => `${indent}  ${b}`)].join("\n"),
        );
      }
      break;

    case "table": {
      const rows = node.content ?? [];
      const lines: string[] = [];
      for (let r = 0; r < rows.length; r++) {
        const cells = rows[r]?.content ?? [];
        const texts = cells.map((cell) =>
          collectMarkdownBlocks(cell, preserveMentionLinks)
            .join(" ")
            .replace(/\|/g, "\\|")
            .replace(/\n/g, " "),
        );
        lines.push(`| ${texts.join(" | ")} |`);
        if (r === 0) {
          lines.push(`| ${texts.map(() => "---").join(" | ")} |`);
        }
      }
      blocks.push(lines.join("\n"));
      break;
    }

    case "horizontalRule":
      blocks.push("---");
      break;

    default:
      // Unknown block — recurse into children
      for (const child of node.content ?? []) {
        blocks.push(...collectMarkdownBlocks(child, preserveMentionLinks, listDepth));
      }
      break;
  }

  return blocks.filter((b) => b.length > 0 || node.type === "paragraph");
}

function applyMarks(text: string, marks?: Mark[]): string {
  if (!marks?.length) return text;
  let result = text;
  for (const mark of marks) {
    switch (mark.type) {
      case "bold":
        result = `**${result}**`;
        break;
      case "italic":
        result = `*${result}*`;
        break;
      case "strike":
        result = `~~${result}~~`;
        break;
      case "code":
        result = `\`${result}\``;
        break;
      case "link": {
        const href = String(mark.attrs?.href ?? "");
        result = `[${result}](${href})`;
        break;
      }
    }
  }
  return result;
}

function collectInlineMarkdown(node: JSONContent, preserveMentionLinks: boolean): string {
  const parts: string[] = [];

  for (const child of node.content ?? []) {
    if (child.type === "text" && typeof child.text === "string") {
      parts.push(applyMarks(child.text, child.marks));
    } else if (child.type === MENTION_NODE) {
      if (preserveMentionLinks) {
        const label = getMentionLabel(child.attrs);
        const id = String(child.attrs?.id ?? "");
        const mentionType = getMentionTypeFromAttrs(child.attrs);
        parts.push(mentionLinkMarkdown(id, label, mentionType));
      } else {
        parts.push(getMentionLabel(child.attrs));
      }
    } else if (child.type === "hardBreak") {
      parts.push("\\\n");
    } else if (Array.isArray(child.content)) {
      parts.push(collectInlineMarkdown(child, preserveMentionLinks));
    }
  }

  return parts.join("");
}
